import { max, min, multiplyWithFactor, multiplyWithPercent, multiplyWithRate, round, subtract, } from "@pentacode/openapi";
import { BonusCompType, BonusTypeMode } from "../model";
import { getAncillaryCostFactor, getHourlyRate } from "../payroll";
import { getContractForDate, getNextDay, parseTime } from "../utils";
import { hour } from "./consts";
import { overlap, subtractAllIntervals, union } from "./interval";
export function getBonusResults(settings, company, employee, entry, work) {
    const contract = getContractForDate(employee.contracts, entry.date);
    if (!contract) {
        return [];
    }
    const bonuses = [];
    for (const bonus of contract.bonuses) {
        const type = company.bonusTypes?.find((t) => t.id === bonus.typeId);
        if (!type || (bonus.positionId && entry.positionId !== bonus.positionId)) {
            continue;
        }
        const mustStartBefore = type.shiftMustStartBefore && parseTime(entry.date, type.shiftMustStartBefore);
        if (mustStartBefore && entry.start >= mustStartBefore) {
            continue;
        }
        const matchingDates = type.mode === BonusTypeMode.Daily
            ? [entry.date]
            : [entry.date, getNextDay(entry.date)].filter((date) => type.matchesDate(date, company.country));
        let intervals = [];
        for (const date of matchingDates) {
            intervals.push(...type.intervals.map(([start, end]) => [parseTime(date, start), parseTime(date, end)]));
        }
        if (!intervals.length) {
            continue;
        }
        intervals = union(intervals);
        bonuses.push({
            bonus,
            type,
            intervals,
        });
    }
    for (const bonus of bonuses) {
        for (const override of bonus.type.overrides) {
            const otherBonuses = bonuses.filter((b) => b.type.id === override.objectId);
            for (const otherBonus of otherBonuses) {
                otherBonus.intervals = subtractAllIntervals(otherBonus.intervals, bonus.intervals);
            }
        }
    }
    const workDuration = ((work[1].getTime() - work[0].getTime()) / hour);
    const results = [];
    for (const { bonus, type, intervals } of bonuses) {
        let duration = overlap(work, intervals);
        if ((type.minDuration && duration < type.minDuration) ||
            (type.minDurationPercent && (duration / workDuration) * 100 < type.minDurationPercent)) {
            duration = 0;
        }
        let hourlyRate;
        switch (type.compType) {
            case BonusCompType.FixedAmount:
                results.push({
                    bonusId: bonus.id,
                    type: {
                        id: type.id,
                        name: type.name,
                        legalType: type.legalType,
                    },
                    taxFree: type.taxFree,
                    ancillaryCosts: 0,
                    duration,
                    wages: bonus.fixedAmount || type.fixedAmountDefault || 0,
                });
                continue;
            case BonusCompType.FixedHourlyRate:
                hourlyRate = bonus.hourlyRate || type.fixedHourlyRateDefault || 0;
                results.push({
                    bonusId: bonus.id,
                    type: {
                        id: type.id,
                        name: type.name,
                        legalType: type.legalType,
                    },
                    taxFree: type.taxFree,
                    ancillaryCosts: 0,
                    duration,
                    hourlyRate,
                    wages: multiplyWithRate(round(duration, 2), hourlyRate),
                });
                continue;
            case BonusCompType.ModifiedHourlyRate: {
                hourlyRate = getHourlyRate(settings, company.benefitTypes, contract, entry.positionId);
                const percent = bonus.percent || type.defaultPercent || 0;
                const taxFreeUpToRate = type.taxFree ? type.taxFreeUpToRate : 0;
                const taxFreeRate = hourlyRate && min(hourlyRate, taxFreeUpToRate);
                const taxedRate = hourlyRate && max(0, subtract(hourlyRate, taxFreeUpToRate));
                // For workdays, we split the bonus into two parts:
                // 1. Tax-free part up to the tax-free rate
                // 2. Taxed part above the tax-free rate
                if (taxFreeRate || (!hourlyRate && !!taxFreeUpToRate)) {
                    const wages = multiplyWithPercent(multiplyWithRate(round(duration, 2), taxFreeRate), percent);
                    results.push({
                        bonusId: bonus.id,
                        type: {
                            id: type.id,
                            name: type.name,
                            legalType: type.legalType,
                        },
                        taxFree: true,
                        hourlyRate: taxFreeRate,
                        ancillaryCosts: 0,
                        wages,
                        duration,
                        percent,
                    });
                }
                if (taxedRate || (!hourlyRate && !taxFreeUpToRate)) {
                    const wages = multiplyWithPercent(multiplyWithRate(round(duration, 2), taxedRate), percent);
                    const ancillaryCosts = multiplyWithFactor(wages, getAncillaryCostFactor(contract.employmentType)[0]);
                    results.push({
                        bonusId: bonus.id,
                        type: {
                            id: type.id,
                            name: type.name,
                            legalType: type.legalType,
                        },
                        taxFree: false,
                        hourlyRate: taxedRate,
                        ancillaryCosts,
                        wages,
                        duration,
                        percent,
                    });
                }
                break;
            }
        }
    }
    return results;
}
//# sourceMappingURL=bonus-result.js.map